import { useEffect, useState } from "react";
import { isAuthed, login, logout, getUser } from "@/lib/kinde";
import { api, type AppsResponse } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AppCard } from "@/components/AppCard";
import { RegionSwitcher } from "@/components/RegionSwitcher";
import { VisitorRegion } from "@/components/VisitorRegion";
import { ScenarioAutostop } from "@/components/ScenarioAutostop";
import { LogOut, RefreshCw } from "lucide-react";

export default function App() {
  const [authState, setAuthState] = useState<"loading" | "out" | "in">("loading");
  const [user, setUser] = useState<{ email?: string; given_name?: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const ok = await isAuthed();
        if (ok) {
          setUser((await getUser()) ?? null);
          setAuthState("in");
        } else {
          setAuthState("out");
        }
      } catch {
        setAuthState("out");
      }
    })();
  }, []);

  if (authState === "loading") {
    return (
      <div className="h-full grid place-items-center text-sm text-[var(--color-muted)]">
        loading…
      </div>
    );
  }

  if (authState === "out") {
    return (
      <div className="h-full grid place-items-center px-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>arbiter-admin</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-[var(--color-muted)]">
              Region switcher + status dashboard for the Trading Bootcamp Fly stack.
              Admin-only.
            </p>
            <Button onClick={login} className="w-full">Sign in with Kinde</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <Dashboard user={user} />;
}

function Dashboard({ user }: { user: { email?: string; given_name?: string } | null }) {
  const [data, setData] = useState<AppsResponse | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    setRefreshing(true);
    try {
      setData(await api.apps());
      setErr(null);
    } catch (e) {
      setErr(String(e));
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-full px-6 py-6 max-w-6xl mx-auto">
      <header className="flex flex-wrap items-center gap-4 mb-8">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">arbiter-admin</h1>
          <VisitorRegion />
        </div>
        <div className="ml-auto flex items-center gap-3 text-sm">
          <span className="text-[var(--color-muted)]">
            {user?.given_name || user?.email || "signed in"}
          </span>
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}>
            <RefreshCw className={refreshing ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
            refresh
          </Button>
          <Button variant="ghost" size="sm" onClick={() => logout()}>
            <LogOut className="h-3.5 w-3.5" /> sign out
          </Button>
        </div>
      </header>

      {err && (
        <div className="mb-4 rounded-md border border-[var(--color-bad)]/30 bg-[var(--color-bad)]/10 px-4 py-2 text-sm text-[var(--color-bad)]">
          {err}
        </div>
      )}

      {data && (
        <>
          <section className="mb-6">
            <RegionSwitcher apps={data.apps} />
          </section>

          <section className="mb-6">
            <ScenarioAutostop apps={data.apps} scenarioApps={data.scenario_apps} />
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.apps.map((a) => (
              <AppCard key={a.name} app={a} />
            ))}
          </section>
        </>
      )}
    </div>
  );
}
